import type { Drawing } from "./drawing";

export class Db {
  static instance: Db;

  private dbPromise?: Promise<IDBDatabase>;

  private constructor() {
    // private to enforce singleton
    this.init().catch((error) => {
      console.error("Failed to initialize database:", error);
    });
  }

  private init(): Promise<IDBDatabase> {
    if (this.dbPromise) {
      return this.dbPromise;
    }
    this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open("colourn", 1);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains("drawings")) {
          db.createObjectStore("drawings", { keyPath: "id" });
        }
      };

      request.onsuccess = (event) => {
        resolve((event.target as IDBOpenDBRequest).result);
      };

      request.onerror = reject;
    });

    return this.dbPromise;
  }

  static getInstance(): Db {
    if (!Db.instance) {
      Db.instance = new Db();
    }
    return Db.instance;
  }

  // new helper: get a store
  private async getStore(storeName: string, mode: IDBTransactionMode) {
    const db = await this.init();
    return db.transaction(storeName, mode).objectStore(storeName);
  }

  // new helper: wrap IDBRequest in a Promise
  private requestPromise<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // updated helper: generic put using inline keyPath 'id'
  private async putEntry(record: {
    id: IDBValidKey;
    [key: string]: unknown;
  }): Promise<void> {
    const store = await this.getStore("drawings", "readwrite");
    const req = store.put(record);
    await this.requestPromise(req);
  }

  // new helper: delete entry by key
  private async deleteEntry(key: IDBValidKey): Promise<void> {
    const store = await this.getStore("drawings", "readwrite");
    const req = store.delete(key);
    await this.requestPromise(req);
  }

  // new helper: generic get by key
  private async getEntry<T>(key: IDBValidKey): Promise<T | undefined> {
    const store = await this.getStore("drawings", "readonly");
    const req = store.get(key);
    return (await this.requestPromise(req)) as T | undefined;
  }

  // new helper: get all saved drawings (excluding inProgress)
  async getAllDrawings(): Promise<Drawing[]> {
    const store = await this.getStore("drawings", "readonly");
    const req = store.getAll();
    const records = (await this.requestPromise(req)) as Array<{
      id: string;
      data: ArrayBuffer;
    }>;
    const all = records
      .filter((r) => r.id !== "inProgress")
      .map((r) => deserializeDrawing(r.data))
      .filter((d) => d.mosaic.length > 0); // filter out any with empty mosaic
    // sort by lastModified desc
    all.sort((a, b) => (b.lastModified ?? 0) - (a.lastModified ?? 0));
    return all;
  }

  // simplified: save drawing + mark in-progress
  async saveToDrawing(drawing: Drawing): Promise<void> {
    drawing.lastModified = Date.now();
    await this.putEntry({ id: drawing.id, data: serializeDrawing(drawing) });
    await this.putEntry({ id: "inProgress", drawingId: drawing.id });
  }

  async setDone(): Promise<void> {
    await this.deleteEntry("inProgress");
  }

  /**
   * Delete a saved drawing by its ID.
   */
  async deleteDrawing(id: string): Promise<void> {
    await this.deleteEntry(id);
  }

  // simplified: get in-progress Drawing or undefined
  async getInProgress(): Promise<Drawing | undefined> {
    const meta = await this.getEntry<{ id: string; drawingId: string }>(
      "inProgress"
    );
    const inProgressId = meta?.drawingId;
    if (!inProgressId) return undefined;
    const record = await this.getEntry<{ id: string; data: ArrayBuffer }>(
      inProgressId
    );
    return record ? deserializeDrawing(record.data) : undefined;
  }
}

export function serializeDrawing(d: Drawing): ArrayBuffer {
  const idBytes = new TextEncoder().encode(d.id);

  // header:
  // [idLen:u32][id:bytes][width:u32][height:u32]
  // [paletteLen:u32][palette:u32 * paletteLen]
  // [mosaicLen:u32][mosaic:u8 * mosaicLen]
  // [clicksLen:u32][clicks:u32 * clicksLen]
  // [showingWordCount:u32][showing:u32 * showingWordCount]

  const width = d.width;
  const height = d.height; // <-- add this to your Drawing type

  const paletteLen = d.palette.length;
  const mosaicLen = d.mosaic.length; // should be width*height
  const clicksLen = d.clicks.length; // number of u32 entries
  const showingWordCount = d.showing.length; // number of u32 words

  const headerSize =
    4 +
    idBytes.length + // idLen + id
    4 +
    4 + // width + height
    4 + // paletteLen
    4 + // mosaicLen
    4 + // clicksLen
    4 + // showingWordCount
    4 + // hint count
    4; // lastModified (4 bytes, good until 2106)

  const paletteBytes = paletteLen * 4;
  const mosaicBytes = mosaicLen; // u8
  const clicksBytes = clicksLen * 4;
  const showingBytes = showingWordCount * 4;

  const buffer = new ArrayBuffer(
    headerSize + paletteBytes + mosaicBytes + clicksBytes + showingBytes
  );
  const view = new DataView(buffer);
  let offset = 0;

  // id
  view.setUint32(offset, idBytes.length, true);
  offset += 4;
  new Uint8Array(buffer, offset, idBytes.length).set(idBytes);
  offset += idBytes.length;

  // dims
  view.setUint32(offset, width, true);
  offset += 4;
  view.setUint32(offset, height, true);
  offset += 4;

  // palette
  view.setUint32(offset, paletteLen, true);
  offset += 4;
  new Uint8Array(buffer, offset, paletteBytes).set(
    new Uint8Array(d.palette.buffer, d.palette.byteOffset, d.palette.byteLength)
  );
  offset += paletteBytes;

  // mosaic
  view.setUint32(offset, mosaicLen, true);
  offset += 4;
  new Uint8Array(buffer, offset, mosaicBytes).set(
    new Uint8Array(d.mosaic.buffer, d.mosaic.byteOffset, d.mosaic.byteLength)
  );
  offset += mosaicBytes;

  // clicks
  view.setUint32(offset, clicksLen, true);
  offset += 4;
  new Uint8Array(buffer, offset, clicksBytes).set(
    new Uint8Array(d.clicks.buffer, d.clicks.byteOffset, d.clicks.byteLength)
  );
  offset += clicksBytes;

  // showing (u32 words)
  view.setUint32(offset, showingWordCount, true);
  offset += 4;
  new Uint8Array(buffer, offset, showingBytes).set(
    new Uint8Array(d.showing.buffer, d.showing.byteOffset, d.showing.byteLength)
  );
  offset += showingBytes;

  // hint count
  view.setUint32(offset, d.hints, true);
  offset += 4;

  // lastModified
  const lm = d.lastModified ? Math.floor(d.lastModified / 1000) : 0; // store as seconds
  view.setUint32(offset, lm, true);
  offset += 4;

  return buffer;
}

export function deserializeDrawing(buffer: ArrayBuffer): Drawing {
  const view = new DataView(buffer);
  let offset = 0;

  // id
  const idLen = view.getUint32(offset, true);
  offset += 4;
  const id = new TextDecoder().decode(new Uint8Array(buffer, offset, idLen));
  offset += idLen;

  // dims
  const width = view.getUint32(offset, true);
  offset += 4;
  const height = view.getUint32(offset, true);
  offset += 4;

  // palette
  const paletteLen = view.getUint32(offset, true);
  offset += 4;
  const paletteBytes = paletteLen * 4;
  const palette = new Uint32Array(buffer.slice(offset, offset + paletteBytes));
  offset += paletteBytes;

  // mosaic
  const mosaicLen = view.getUint32(offset, true);
  offset += 4;
  const mosaic = new Uint8Array(buffer.slice(offset, offset + mosaicLen));
  offset += mosaicLen;

  // clicks
  const clicksLen = view.getUint32(offset, true);
  offset += 4;
  const clicksBytes = clicksLen * 4;
  const clicks = new Uint32Array(buffer.slice(offset, offset + clicksBytes));
  offset += clicksBytes;

  // showing
  const showingWordCount = view.getUint32(offset, true);
  offset += 4;
  const showingBytes = showingWordCount * 4;
  const showing = new Uint32Array(buffer.slice(offset, offset + showingBytes));
  offset += showingBytes;

  // load hint count conditionally, if present
  // (for backward compatibility with older saved drawings)
  let hints = 0;
  if (offset + 4 <= buffer.byteLength) {
    hints = view.getUint32(offset, true);
    offset += 4;
  }

  // load lastModified conditionally, if present
  // (for backward compatibility with older saved drawings)
  let lastModified: number | undefined = undefined;
  if (offset + 4 <= buffer.byteLength) {
    const lm = view.getUint32(offset, true);
    if (lm > 0) {
      // convert from seconds to ms
      lastModified = lm * 1000;
    }
    offset += 4;
  }

  return {
    id,
    width,
    height,
    palette,
    mosaic,
    clicks,
    showing,
    hints,
    lastModified,
  };
}
